import { useEffect, useState, type ReactNode } from 'react';
import { getClient } from '@sakura-grp/database';
import { useAuthStore } from '@/stores/auth';

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * 認証プロバイダー
 * アプリ起動時にセッションを復元し、認証状態の変更を監視
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const { setSession, setLoading } = useAuthStore();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const supabase = getClient();

    // 初回マウント時にセッションを復元
    const initializeAuth = async () => {
      try {
        console.log('[AuthProvider] セッション復元開始');

        // 既存のセッションを取得
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('[AuthProvider] セッション取得エラー:', error);
          setSession(null);
        } else if (session) {
          console.log('[AuthProvider] セッション復元成功:', {
            userId: session.user.id,
            email: session.user.email,
            expiresAt: new Date(session.expires_at! * 1000).toLocaleString(),
          });

          // セッションの有効期限をチェック
          const expiresAt = new Date(session.expires_at! * 1000);
          const now = new Date();
          const isExpired = expiresAt <= now;

          if (isExpired) {
            console.log('[AuthProvider] セッション期限切れ - リフレッシュ試行');
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

            if (refreshError) {
              console.error('[AuthProvider] リフレッシュエラー:', refreshError);
              setSession(null);
            } else {
              console.log('[AuthProvider] リフレッシュ成功');
              setSession(refreshData.session);
            }
          } else {
            setSession(session);
          }
        } else {
          console.log('[AuthProvider] セッションなし');
          setSession(null);
        }
      } catch (err) {
        console.error('[AuthProvider] 初期化エラー:', err);
        setSession(null);
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };

    initializeAuth();

    // 認証状態の変更を監視
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('[AuthProvider] 認証状態変更:', event, {
          userId: session?.user.id,
          email: session?.user.email,
        });

        switch (event) {
          case 'SIGNED_IN':
            setSession(session);
            break;
          case 'SIGNED_OUT':
            setSession(null);
            break;
          case 'TOKEN_REFRESHED':
            console.log('[AuthProvider] トークンリフレッシュ完了');
            setSession(session);
            break;
          case 'USER_UPDATED':
            setSession(session);
            break;
          default:
            // INITIAL_SESSION等のイベントは無視（initializeAuthで処理済み）
            break;
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [setSession, setLoading]);

  // 初期化完了まではローディング表示
  if (!initialized) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="size-8 animate-spin rounded-full border-4 border-brand border-t-transparent mx-auto mb-4" />
          <p className="text-sm text-gray-500">読み込み中...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
